import React, { useEffect, useState, useRef } from 'react';
import { Dialog, Grid, Typography, Box, Button, Divider } from '@mui/material';
import { Document, Page } from 'react-pdf';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import CustomSignaturePadDialog from './CustomSignaturePadDialog';
import { poppins400, poppins500 } from 'theme/common.style';
import { postApiCall } from 'api/apiService';
import {
  API_URL,
  BUYER_CONTRACT_OF_SALE,
  VENDOR_CONTRACT_OF_SALE,
} from 'constant/app.config';
import Swal from 'sweetalert2';
import Loading from 'assets/images/loading.png';
import { useParams } from 'react-router-dom';

function CommenceESignDialog({
  open,
  onClose,
  pdfMeta,
  streetName,
  matchessm,
  userDetail,
  propertyId,
  getListInfo,
}) {
  const BUYER_PAGE_WIDTH = matchessm ? 320 : 800;
  const pageViewports = useRef({});
  const pageRefs = useRef({});
  const pageDimensions = useRef({}); // ✅ ADDED: Store original PDF dimensions

  const [numPages, setNumPages] = useState(0);
  const [signatureBase64, setSignatureBase64] = useState(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState(null);
  const [openPad, setOpenPad] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [isManualSignature, setIsManualSignature] = useState(false);
  const [isApplyingSignature, setIsApplyingSignature] = useState(false);

  // ✅ MANDATORY CHANGE: unmount Document before loading signed PDF
  const [showPdf, setShowPdf] = useState(true);

  const pdfSource = signedPdfUrl || pdfMeta.contractFile;
  const hasSignature = Boolean(signatureBase64);
  const canUpdateSignature = hasSignature;
  const params = useParams();

  // ✅ UPDATED: Field data should now contain percentages from API
  const isBuyer = userDetail?.primaryUserType === 'Buyer';

  const fields =
    pdfMeta?.fields?.filter((f) => {
      if (!isBuyer) {
        return true;
      }

      return (
        Number(f.signUserId) === 0 ||
        Number(f.signUserId) === Number(userDetail?.userId)
      );
    }) || [];

  const removeWhiteBackground = async (signature) => {
    let imgSrc = signature;

    // ✅ If URL, fetch it as blob first
    if (!signature.startsWith('data:image')) {
      const response = await fetch(signature, { mode: 'cors' });
      const blob = await response.blob();
      imgSrc = URL.createObjectURL(blob);
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // IMPORTANT
      img.src = imgSrc;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          if (r > 230 && g > 230 && b > 230) {
            data[i + 3] = 0;
          } else if (r > 200 && g > 200 && b > 200) {
            data[i + 3] = 50;
          } else if (r < 100 && g < 100 && b < 100 && a > 0) {
            data[i + 3] = 255;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        resolve(pngDataUrl);
      };
    });
  };

  useEffect(() => {
    const sessionSign = sessionStorage.getItem('buyerSignature');
    if (sessionSign) {
      const parsed = JSON.parse(sessionSign);

      if (parsed.userId !== userDetail?.userId) {
        sessionStorage.removeItem('buyerSignature');
        setSignatureBase64(null);
        setIsManualSignature(false);
        return;
      }

      setSignatureBase64(parsed.base64);
      setIsManualSignature(false);
      return;
    }

    if (userDetail?.signUrl) {
      setSignatureBase64(userDetail.signUrl);
      setIsManualSignature(false);
      sessionStorage.setItem(
        'buyerSignature',
        JSON.stringify({
          userId: userDetail.userId,
          base64: userDetail.signUrl,
          timestamp: new Date().toISOString(),
        })
      );
    } else {
      setSignatureBase64(null);
      setIsManualSignature(false);
    }
  }, [userDetail?.userId, userDetail?.signUrl]);

  useEffect(() => {
    if (confirmLoading) {
      document.body.style.pointerEvents = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [confirmLoading]);

  const getSydneyDate = () => {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(new Date());

    const day = parts.find((p) => p.type === 'day')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const year = parts.find((p) => p.type === 'year')?.value;

    return `${day}-${month}-${year}`;
  };

  // ✅ UPDATED: Apply signature with percentage-based coordinates
  const applySignature = async (signatureOverride) => {
    setIsApplyingSignature(true);
    const signature = signatureOverride ?? signatureBase64;

    if (!signature) {
      console.warn('No signature available to apply');
      setIsApplyingSignature(false);
      return;
    }

    try {
      const pdfBytes = await fetch(pdfMeta.contractFile).then((r) =>
        r.arrayBuffer()
      );

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      const transparentSignature = await removeWhiteBackground(signature);
      const imgBytes = await getSignatureBytes(transparentSignature);

      const image = await pdfDoc.embedPng(imgBytes);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      fields.forEach((f) => {
        const page = pages[f.page - 1];

        if (!page) {
          return;
        }

        const { width: pdfW, height: pdfH } = page.getSize();

        // ✅ CHANGED: Use percentage-based coordinates
        const pageDim = pageDimensions.current[f.page];
        if (!pageDim) {
          console.warn(`No dimensions for page ${f.page}`);
          return;
        }

        // Convert percentages to PDF coordinates
        const pdfX = f.x_percent * pdfW;
        const pdfY = pdfH - (f.y_percent + f.height_percent) * pdfH;

        if (f.type === 'signature') {
          page.drawImage(image, {
            x: pdfX,
            y: pdfY,
            width: f.width_percent * pdfW,
            height: f.height_percent * pdfH,
          });
        }

        if (f.type === 'date') {
          page.drawText(getSydneyDate(), {
            x: pdfX + 6,
            y: pdfY + (f.height_percent * pdfH) / 2 - 5,
            size: 12,
            font,
            color: rgb(0, 0, 0),
          });
        }
      });

      const signedBytes = await pdfDoc.save();

      // ============================================================
      // ✅ MANDATORY FIX
      // Unmount the existing react-pdf Document first.
      // This allows PDF.js to destroy the old worker/loading task.
      // ============================================================
      setShowPdf(false);

      const newSignedPdfUrl = URL.createObjectURL(
        new Blob([signedBytes], {
          type: 'application/pdf',
        })
      );

      // Wait for the old Document to unmount before mounting
      // the newly signed PDF.
      setTimeout(() => {
        setSignedPdfUrl(newSignedPdfUrl);
        setNumPages(0);
        setPdfReady(false);
        setShowPdf(true);
      }, 300);

      setIsApplied(true);
    } catch (error) {
      console.error('Error applying signature:', error);
    } finally {
      setIsApplyingSignature(false);
    }
  };

  const getPrimaryLabel = () => {
    if (!signatureBase64) return 'Apply signature';
    if (!isApplied) return 'Apply signature';
    return 'Confirm';
  };

  const confirmContractOfSale = async () => {
    setConfirmLoading(true);

    try {
      const userType = userDetail?.primaryUserType;

      const endpoint =
        userType === 'Vendor'
          ? VENDOR_CONTRACT_OF_SALE
          : BUYER_CONTRACT_OF_SALE;

      const { data } = await postApiCall(`${endpoint}`, {
        propertyId,
        userType,
      });

      if (data?.status !== 1) {
        await Swal.fire({
          icon: 'error',
          title: data?.heading || 'Error',
          text:
            data?.message ||
            'Unable to confirm the contract. Please try again.',
          confirmButtonText: 'Close',
        });

        return;
      }

      setConfirmLoading(false);
      onClose();

      await Swal.fire({
        icon: 'success',
        title: data?.heading || 'Success',
        text: data?.message || '',
        confirmButtonText: 'Close',
      });

      if (userType == 'Vendor') {
        getListInfo('apiupdated', params?.dashboardId);
      } else {
        getListInfo();
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    const handleSignatureUpdate = (event) => {
      // Check if this update is for the current user
      if (event.detail?.userId === userDetail?.userId) {
        // Refresh signature from sessionStorage
        const sessionSign = sessionStorage.getItem('buyerSignature');

        if (sessionSign) {
          const parsed = JSON.parse(sessionSign);

          if (parsed.userId === userDetail?.userId) {
            setSignatureBase64(parsed.base64);
            // Reset applied state to allow re-application if needed
            setIsApplied(false);
            setSignedPdfUrl(null);
          }
        } else if (event.detail?.signUrl) {
          // Fall back to the URL from the event
          setSignatureBase64(event.detail.signUrl);
          setIsApplied(false);
          setSignedPdfUrl(null);
        }
      }
    };

    window.addEventListener('signatureUpdated', handleSignatureUpdate);

    return () => {
      window.removeEventListener('signatureUpdated', handleSignatureUpdate);
    };
  }, [userDetail?.userId]);

  const handlePrimaryAction = () => {
    if (!signatureBase64) {
      setOpenPad(true);
      return;
    }

    if (!isApplied) {
      applySignature();
      return;
    }

    confirmContractOfSale();
  };

  const getSignatureBytes = async (signature) => {
    if (signature.startsWith('data:image')) {
      const base64 = signature.split(',')[1];
      return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    }

    const res = await fetch(signature);
    return new Uint8Array(await res.arrayBuffer());
  };

  const PdfLoader = ({ height }) => (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img src={Loading} alt="Loading PDF" style={{ width: 40, height: 40 }} />
    </Box>
  );

  // ✅ ADDED: Store original PDF dimensions
  const handlePageLoadSuccess = (page, pdfPage) => {
    const viewport = pdfPage.getViewport({ scale: 1 });
    const scale = BUYER_PAGE_WIDTH / viewport.width;

    // Store original PDF dimensions (in pixels at scale 1)
    pageDimensions.current[page] = {
      width: viewport.width,
      height: viewport.height,
    };

    // Store display dimensions
    pageViewports.current[page] = {
      scale,
      height: viewport.height * scale,
    };

    setPdfReady(true);
  };

  // ✅ ADDED: Calculate display position from percentages
  const calculateFieldDisplayPosition = (field, pageNum) => {
    const vp = pageViewports.current[pageNum];
    const pageDim = pageDimensions.current[pageNum];

    if (!vp || !pageDim) {
      console.warn(`No viewport or dimensions for page ${pageNum}`);
      return null;
    }

    // Convert percentages to display pixels
    const displayX = field.x_percent * pageDim.width * vp.scale;
    const displayY = field.y_percent * pageDim.height * vp.scale;
    const displayWidth = field.width_percent * pageDim.width * vp.scale;
    const displayHeight = field.height_percent * pageDim.height * vp.scale;

    return {
      x: displayX,
      y: displayY,
      width: displayWidth,
      height: displayHeight,
    };
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth={false}
        fullScreen={matchessm}
        PaperProps={{
          sx: {
            borderRadius: matchessm ? 0 : '5px',
            width: matchessm ? '100vw' : '90vw',
            maxWidth: matchessm ? '100vw' : '1200px',
            height: matchessm ? '100vh' : 'auto',
            margin: 0,
            padding: matchessm ? '20px' : '30px',
          },
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography
              sx={{
                ...poppins400,
                fontSize: matchessm ? '10px' : '14px',
                color: '#666666',
              }}
            >
              {streetName}
            </Typography>
          </Grid>

          <Grid item xs={12} display="flex" alignItems="center" mt={1}>
            <Pendingwarning />
            <Typography
              ml={1}
              sx={{
                ...poppins500,
                fontSize: matchessm ? '16px' : '24px',
                paddingLeft: '12px',
              }}
            >
              Commence E-Sign
            </Typography>
          </Grid>

          <Grid item xs={12} mt={1}>
            <Typography
              sx={{
                ...poppins400,
                fontSize: matchessm ? '12px' : '16px',
                color: '#666666',
                lineHeight: '22px',
              }}
            >
              Review the document and apply your signature and date.
            </Typography>
          </Grid>

          <Grid item xs={12} mt={2}>
            <Divider />
          </Grid>

          <Grid item xs={12} mt={2}>
            <Box
              sx={{
                border: '1px solid rgba(17,35,71,0.12)',
                borderRadius: '6px',
                background: '#FAFAFA',
                padding: '12px',
              }}
            >
              <Box
                sx={{
                  height: matchessm ? '55vh' : '65vh',
                  overflow: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {/* =====================================================
                    ✅ MANDATORY FIX:
                    Completely remove the old Document before loading
                    the newly signed PDF.
                   ===================================================== */}
                {showPdf && (
                  <Document
                    file={pdfSource}
                    loading={
                      <div className="loaderImage">
                        <PdfLoader height={matchessm ? '55vh' : '65vh'} />
                      </div>
                    }
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  >
                    {Array.from({ length: numPages }, (_, i) => {
                      const page = i + 1;

                      return (
                        <Box
                          key={page}
                          position="relative"
                          mb={3}
                          ref={(el) => (pageRefs.current[page] = el)}
                        >
                          <Page
                            pageNumber={page}
                            width={BUYER_PAGE_WIDTH}
                            loading={
                              <PdfLoader className="loaderImage" height={400} />
                            }
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onLoadSuccess={(pdfPage) =>
                              handlePageLoadSuccess(page, pdfPage)
                            }
                          />

                          {!isApplied &&
                            pdfReady &&
                            fields
                              .filter((f) => f.page === page)
                              .map((f) => {
                                const position = calculateFieldDisplayPosition(
                                  f,
                                  page
                                );

                                if (!position) return null;

                                const pageEl = pageRefs.current[page];
                                if (!pageEl) return null;

                                const canvas = pageEl.querySelector('canvas');

                                const canvasTop = canvas ? canvas.offsetTop : 0;

                                const canvasLeft = canvas
                                  ? canvas.offsetLeft
                                  : 0;

                                return (
                                  <Box
                                    key={f.fieldId}
                                    position="absolute"
                                    left={canvasLeft + position.x}
                                    top={canvasTop + position.y}
                                    width={position.width}
                                    height={position.height}
                                    borderRadius="6px"
                                    bgcolor={
                                      !isApplied ? '#6BBBE8' : 'transparent'
                                    }
                                    sx={{
                                      border: '2px dashed #4CA4D6',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: isApplyingSignature
                                        ? 'default'
                                        : 'pointer',
                                      opacity: isApplyingSignature ? 0.7 : 1,
                                    }}
                                    onClick={() => {
                                      if (!isApplyingSignature) {
                                        handlePrimaryAction();
                                      }
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontFamily: 'Poppins',
                                        fontWeight: matchessm ? 300 : 600,
                                        color: '#fff',
                                        fontSize: matchessm && '8px',
                                      }}
                                    >
                                      {f.type === 'signature'
                                        ? 'Sign Here'
                                        : 'Date'}
                                    </Typography>
                                  </Box>
                                );
                              })}
                        </Box>
                      );
                    })}
                  </Document>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            mt={3}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                padding: matchessm ? '6px 18px' : '10px 39px',
                height: matchessm ? '35px' : '41px',
                fontSize: matchessm ? '12px' : '14px',
                color: '#666666',
                fontFamily: 'Poppins',
                border: '1px solid rgba(17, 35, 71, 0.1)',
                borderRadius: '5px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(17, 35, 71, 0.1)',
                },
              }}
            >
              Cancel
            </Button>

            {isApplied && (
              <Button
                variant="outlined"
                onClick={() => {
                  setIsApplied(false);
                  setSignedPdfUrl(null);
                  setShowPdf(false);
                  setOpenPad(true);
                }}
                sx={{
                  padding: matchessm ? '7px 17px' : '10px 39px',
                  height: matchessm ? '35px' : '41px',
                  fontSize: matchessm ? '12px' : '14px',
                  color: '#FFFFFF',
                  fontFamily: 'Poppins',
                  background: '#4CA4D6',
                  borderRadius: '5px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4CA4D6',
                  },
                }}
              >
                Update signature
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handlePrimaryAction}
              disabled={confirmLoading || isApplyingSignature}
              sx={{
                padding: matchessm ? '7px 17px' : '10px 39px',
                height: matchessm ? '35px' : '41px',
                fontSize: matchessm ? '12px' : '14px',
                color: '#FFFFFF',
                fontFamily: 'Poppins',
                background: '#4CA4D6',
                borderRadius: '5px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#4CA4D6',
                },
                '&:disabled': {
                  background: '#BFDDF0',
                  color: '#FFFFFF',
                },
              }}
            >
              {getPrimaryLabel()}
            </Button>

            {(confirmLoading || isApplyingSignature) && (
              <img
                alt="Loader"
                src={Loading}
                className="loaderImage"
                style={{
                  height: '32px',
                  width: '32px',
                  marginLeft: '8px',
                }}
              />
            )}
          </Grid>
        </Grid>
      </Dialog>

      {openPad && (
        <CustomSignaturePadDialog
          open
          userDetail={userDetail}
          onClose={() => {
            setOpenPad(false);
            setShowPdf(true);
          }}
          onSave={(base64) => {
            setSignatureBase64(base64);
            setIsManualSignature(true);
            setIsApplied(false);
            setSignedPdfUrl(null);
            setOpenPad(false);

            sessionStorage.setItem(
              'buyerSignature',
              JSON.stringify({
                userId: userDetail.userId,
                base64: base64,
                timestamp: new Date().toISOString(),
              })
            );
          }}
          onApplied={async (base64) => {
            setIsManualSignature(true);
            await applySignature(base64);
            setOpenPad(false);
          }}
        />
      )}
    </>
  );
}

export default CommenceESignDialog;
